import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import type { ResponseCreateLocation, ResponseInviteDriver } from '../../../../../app/ApiGen';
import { useCreateJob, useCreateLocation, useInviteDriver } from '../../../../../app/ApiGen';
import { errorSnackbar } from '../../../../../components/ErrorSnackbar';
import { FormReadonly } from '../../../../../components/Form/components/FormReadonly';
import { WizardConfirmationStep } from '../../../../../components/Wizard/WizardConfirmationStep';
import { Section, useWizard } from '../../../../../design-system';
import { type EvnexJobFormDetails, evnexJobFormFields } from '../../../../../utils/jobs/CreateEvnexJob';
import { type JobLocationDetails, jobLocationFields } from '../../../../../utils/jobs/location';
import {
  mapJobWizardStateToRequestCreateJob,
  mapJobWizardStateToRequestCreateLocation,
  mapJobWizardStateToRequestInviteDriver,
} from '../../../../../utils/jobs/mappers';
import { type UserCreateDetails, userCreateFields } from '../../../../../utils/users/FieldDefinitions';
import { type JobWizardState } from '../types';
import { getOrganisationFields, type OrganisationDetails } from './OrganisationStep';

export const ConfirmationStep: React.FC = () => {
  const { push } = useHistory();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const { state: untypedState, nextStep } = useWizard();
  const state = untypedState as JobWizardState;

  const jobDetailsFields = evnexJobFormFields({ installerOptions: state.assignedInstallers });

  const inviteDriver = useInviteDriver({ orgSlug: state.organisation.slug });
  const createLocation = useCreateLocation({ orgSlug: state.organisation.slug });
  const createJob = useCreateJob({});

  const redirectLocation = '/admin/jobs';

  const createJobLocation = React.useCallback(async (): Promise<ResponseCreateLocation['data']['id'] | undefined> => {
    try {
      const { data: location } = await createLocation.mutate(mapJobWizardStateToRequestCreateLocation(state));
      enqueueSnackbar(`Location created`, { variant: 'success' });
      return location.id;
    } catch (e) {
      errorSnackbar('Unable to create location', closeSnackbar, enqueueSnackbar, e);
      return undefined;
    }
  }, [closeSnackbar, createLocation, enqueueSnackbar, state]);

  const createJobCustomer = React.useCallback(async (): Promise<ResponseInviteDriver['data']['id'] | undefined> => {
    try {
      const { data: customer } = await inviteDriver.mutate(mapJobWizardStateToRequestInviteDriver(state));
      enqueueSnackbar(`Customer invited to ${state.organisation.name}`, { variant: 'success' });
      return customer.id;
    } catch (e) {
      errorSnackbar('Unable to create user account', closeSnackbar, enqueueSnackbar, e);
      return undefined;
    }
  }, [inviteDriver, state, enqueueSnackbar, closeSnackbar]);

  const createJobAndGoToNextStep = React.useCallback(
    async (args: { driverId: string; locationId: string }) => {
      const { driverId, locationId } = args;
      await createJob.mutate(mapJobWizardStateToRequestCreateJob({ ...state, driverId, locationId }));
      enqueueSnackbar(`Home Installation job created`, { variant: 'success' });
      push(redirectLocation);
      nextStep();
    },
    [createJob, enqueueSnackbar, nextStep, push, state],
  );

  const onSubmit = React.useCallback(async () => {
    const driverId = await createJobCustomer();
    if (!driverId) {
      return;
    }
    const locationId = await createJobLocation();
    if (!locationId) {
      return;
    }
    await createJobAndGoToNextStep({ driverId, locationId });
  }, [createJobAndGoToNextStep, createJobCustomer, createJobLocation]);

  return (
    <WizardConfirmationStep itemBeingCreated="job" onSubmit={onSubmit}>
      <Section title="Organisation details">
        <FormReadonly<OrganisationDetails>
          fields={getOrganisationFields([state.organisation])}
          initialValues={{ organisation: state.organisation }}
        />
      </Section>
      <Section title="Customer details">
        <FormReadonly<UserCreateDetails> fields={userCreateFields} initialValues={state.driverFieldContents} />
      </Section>
      <Section title="New location details">
        <FormReadonly<JobLocationDetails> fields={jobLocationFields({})} initialValues={state.locationFieldContents} />
      </Section>
      <Section title="Job details">
        <FormReadonly<EvnexJobFormDetails> fields={jobDetailsFields} initialValues={state} />
      </Section>
    </WizardConfirmationStep>
  );
};
